import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const LABELS = { job_number: "job number", moving_date: "date", start_time: "start time", customer_name: "name", mobile: "mobile", email: "email", pickup_address: "pickup",
  delivery_address: "delivery", state: "state", bedrooms: "bedrooms", men: "men", cubic_metres: "cubic metres", truck_size: "truck size", job_notes: "notes", pricing_type: "pricing type",
  hourly_rate: "hourly rate", callout_hours: "call-out", estimated_hours: "est. hours", fixed_price: "fixed price", price_per_m3: "rate/m³", deposit_amount: "deposit", deposit_method: "deposit method",
  sales_person: "sales person", lead_source: "lead source", bill_to_company: "bill to", job_type: "route", confirmation_time: "arrival window", emergency_contact_number: "emergency contact" };

export const AiAutofill = ({ form, setForm, settings }) => {
  const [open, setOpen] = useState(!form.customer_name);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [filled, setFilled] = useState([]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/extract", { text });
      const f = data.fields || {};
      const companies = settings.companies.map((c) => c.name);
      const next = { ...form };
      const keys = [];
      Object.entries(f).forEach(([k, v]) => {
        if (!(k in next) && k !== "bill_to_company") return;
        next[k] = typeof v === "number" ? String(v) : v;
        keys.push(k);
      });
      if (f.lead_source && companies.find((c) => c.toLowerCase() === String(f.lead_source).toLowerCase())) next.bill_to_company = companies.find((c) => c.toLowerCase() === String(f.lead_source).toLowerCase());
      if (f.bill_to_company && !next.lead_source) next.lead_source = f.bill_to_company;
      setForm(next);
      setFilled(keys);
      toast.success(keys.length ? `Filled ${keys.length} field${keys.length > 1 ? "s" : ""} – please check them` : "Couldn't find booking details in that text");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#0E1E38]/15 bg-gradient-to-br from-[#0E1E38] to-[#1a3058] p-4 text-white" data-testid="ai-autofill">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left" data-testid="ai-autofill-toggle">
        <span className="inline-flex items-center gap-2 font-display text-sm font-semibold"><Sparkles className="h-4 w-4 text-[#FACC15]" /> Paste details – AI fills the form</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={"Paste the customer's email, SMS, WhatsApp or the partner job sheet here…\ne.g. \"Hi, John Smith 0412 345 678, moving 3 bed house from 12 Smith St Parramatta to 4 Jones Rd Penrith on Sat 25 Oct, 8am, $150/hr + 1hr callout, $200 deposit paid by card\""}
            className="bg-white text-slate-900 placeholder:text-slate-400" data-testid="ai-autofill-text" />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={run} disabled={busy || text.trim().length < 10} data-testid="ai-autofill-run" className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Reading…" : "Autofill booking"}
            </Button>
            <span className="text-xs text-sky-100/80">Only fills what it finds – always double-check before saving.</span>
          </div>
          {filled.length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="ai-autofill-filled">{filled.map((k) => <span key={k} className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-medium">{LABELS[k] || k}</span>)}</div>}
        </div>
      )}
    </div>
  );
};
